/**
 * P17 — **D260(d): the Places call leaves the iOS WebView through D166's door, and only on iOS.**
 *
 * **What this is not.** Capacitor ships a config flag (`plugins.CapacitorHttp.enabled`) that patches
 * `window.fetch` and `XMLHttpRequest` app-wide, and **it stays off**. D166 declines it *because*
 * turning it on would silently reroute this very client, and **D260(d) names turning it on as the
 * edit to refuse**: it would move the one authenticated door, every timeout and the whole error
 * taxonomy onto the native stack in order to buy two address lookups, by a one-line config change
 * with no test that would go red. What moves here is **two calls**, through the `fetchImpl` seam
 * `suggestPlaces()` and `resolvePlace()` already take. The global `fetch` is left exactly as the
 * platform supplied it.
 *
 * **Why iOS needs it at all.** The iOS WebView's origin is `capacitor://<host>` (D161(c) —
 * `iosScheme: 'https'` raises at launch, so this is a platform fact rather than a configuration
 * choice). Android's origin is byte-identical to the web origin, so its WebView's own `fetch`
 * reaches Google exactly as a browser's does and **must not** be rerouted: that would trade a
 * working path for an untested one, which is `usesNativeHttp()`'s own stated rule.
 *
 * **The platform predicate is imported, never restated.** `usesNativeHttp()` already answers *iOS,
 * in a native container*, and a second copy here would be a second answer to one question — the
 * shape §4.6 warns about wherever a value is decided in one place and spelled in another. That it
 * lives in `auth/` is incidental: it is a fact about the platform, not about the API.
 *
 * **Reached through the injected global, never an import.** `web/` must not depend on
 * `@capacitor/core`; the shell is P6's and the container is P18's, and `native/` consumes the built
 * bundle rather than the other way round. `auth/nativeHttp.ts` says the same thing about the same
 * global for the same reason, including why the member read is `Capacitor.nativePromise` and why
 * `Capacitor.Plugins.CapacitorHttp` would be `undefined` in a bundle that refuses that import.
 */

import { usesNativeHttp } from '../auth/nativeHttp'
import { TIMED_OUT, deadline } from '../auth/timeout'

/**
 * 🔸 The budget one Places call gets on the native stack.
 *
 * Shorter than `auth/timeout.ts`'s `REQUEST_TIMEOUT_MS` deliberately: this call sits under a
 * keystroke and its result is discarded the moment the query moves on, so a caller freed late is a
 * suggestion list that arrives after the user has stopped looking. It is **not** shared with the API
 * budget — these are different questions with different costs, and `AGENTS.md` §4.4's rule that each
 * guard states its own reason applies to a client timeout as much as to a broker one.
 */
export const MAPS_REQUEST_TIMEOUT_MS = 10_000

interface CapacitorBridge {
  nativePromise?: (pluginName: string, methodName: string, options: unknown) => Promise<unknown>
}

function bridge(): CapacitorBridge | undefined {
  return (globalThis as typeof globalThis & { Capacitor?: CapacitorBridge }).Capacitor
}

/** Whether this launch must send the Places call down the native stack. iOS container only. */
export function usesNativeMapsHttp(): boolean {
  return usesNativeHttp()
}

function headersOf(init: RequestInit | undefined): Record<string, string> {
  const raw = init?.headers
  if (raw === undefined) return {}
  if (raw instanceof Headers) return Object.fromEntries(raw.entries())
  if (Array.isArray(raw)) return Object.fromEntries(raw)
  return { ...(raw as Record<string, string>) }
}

/**
 * A `fetch`-shaped adapter over `CapacitorHttp`, returning a **real `Response`** so every caller
 * property `places.ts` reads — `ok`, `status`, `json()` — is the platform's own implementation
 * rather than a hand-rolled stand-in that has to be kept honest. That is what lets one set of
 * response-handling code in `places.ts` serve both stacks, which is the property `nativeHttp.ts`
 * calls *indistinguishable to every caller*.
 *
 * **A status that arrives is passed through untouched.** The plugin resolves for every HTTP status
 * and rejects only on a transport-level failure, so a `403` from a wrong bundle identifier arrives
 * here as a `403` and reaches `httpFailureReason()` as `denied` — which is the whole point, since
 * inventing a status would erase the one signal the restriction produces.
 *
 * **The abort signal is honoured even though the caller also guards.** `LocationPicker` re-checks
 * `controller.signal.aborted` after every call, so ignoring it would not reorder suggestions — but a
 * native request in flight belongs to the platform and cannot be cancelled, so what an abort buys
 * is freeing the *caller*, not stopping the request. Rejecting with an `AbortError`-shaped error
 * keeps the two stacks indistinguishable at the `catch` in `places.ts`, which maps any throw to
 * `network`.
 */
export const nativeMapsFetch: typeof fetch = async (input, init) => {
  const capacitor = bridge()
  const call = capacitor?.nativePromise
  // Unreachable behind `usesNativeMapsHttp()`. A throw rather than a status, because every caller
  // maps a throw to `network` and there is no honest status to invent for a bridge that vanished.
  if (typeof call !== 'function') throw new Error('capacitor-bridge-absent')

  const signal = init?.signal ?? undefined
  if (signal?.aborted === true) throw new DOMException('Aborted', 'AbortError')

  const budget = deadline(MAPS_REQUEST_TIMEOUT_MS)
  const aborted = new Promise<never>((_resolve, reject) => {
    signal?.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), { once: true })
  })

  try {
    const request = call.call(capacitor, 'CapacitorHttp', 'request', {
      url: typeof input === 'string' ? input : input.toString(),
      method: init?.method ?? 'GET',
      headers: headersOf(init),
      ...(init?.body === undefined || init.body === null ? {} : { data: String(init.body) }),
      connectTimeout: MAPS_REQUEST_TIMEOUT_MS,
      readTimeout: MAPS_REQUEST_TIMEOUT_MS,
    })

    const raw = await Promise.race([budget.settle(request), aborted])
    if (raw === TIMED_OUT) throw new Error('maps-request-timeout')

    const status = readStatus(raw)
    if (status === undefined) throw new Error('native-http-response-unusable')
    return new Response(bodyOf(raw), { status })
  } finally {
    budget.cancel()
  }
}

function readStatus(raw: unknown): number | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const value = (raw as Record<string, unknown>)['status']
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

/**
 * The body, as a string `Response` can re-parse.
 *
 * The native side parses JSON itself when the response carries a JSON content-type and hands back a
 * string otherwise, so an object arrives already parsed and is re-serialized here rather than being
 * handed to `Response` — which would stringify it as `[object Object]`. Re-serializing is safe
 * because `places.ts` only ever reads it back through `.json()`; a string passes through untouched
 * so a non-JSON error page reaches the same `catch` it reaches on the web stack.
 */
function bodyOf(raw: unknown): string | null {
  if (typeof raw !== 'object' || raw === null) return null
  const data = (raw as Record<string, unknown>)['data']
  if (data === undefined || data === null) return null
  return typeof data === 'string' ? data : JSON.stringify(data)
}

/** The `fetch` this platform's Places call should use. */
export function mapsFetch(): typeof fetch {
  return usesNativeMapsHttp() ? nativeMapsFetch : fetch
}
